import React, { useEffect, useState } from 'react';
import {
  type StorefrontInfo,
  storefrontInfoContext,
} from './storefront-info-context';
import wretch from 'wretch';
import { Helmet } from 'react-helmet-async';
import { useBuildVars } from '../../../config/build-config';
import { useSiteConfig } from '../../../config/site-config';

const apiWretcher = wretch(undefined).options({
  // https://developer.mozilla.org/en-US/docs/Web/API/Request/credentials
  // credentials: 'include',
  credentials: 'same-origin', // Default

  headers: {
    accept: 'application/json',
    'content-type': 'application/json',
  },

  // https://developer.mozilla.org/en-US/docs/Web/API/Request/mode
  mode: 'cors',
});

type StorefrontInfoProviderProps = {
  children: React.ReactNode;
};

export function StorefrontInfoProvider(
  props: Readonly<StorefrontInfoProviderProps>
) {
  const { children } = props;
  const [value, setValue] = useState<StorefrontInfo | undefined>(undefined);
  const buildVars = useBuildVars();
  const siteConfig = useSiteConfig();

  useEffect(() => {
    let active = true;

    const fetchStorefrontInfo = async () => {
      let domainName: string;

      if (globalThis.location.origin.indexOf('://local') > -1) {
        // Accounts for local env, even when stage and production builds locally in express or nginx.
        domainName = buildVars.X4EC_DEV_PROXY_HOSTNAME;
      } else {
        domainName = globalThis.location.hostname;
      }

      const wretcher = apiWretcher
        .url(
          'https://im-qacep-us1.ingrammicro.com/resellermanagement.webapi.marketplace/api/v1/marketplaces'
        )
        .query({ domainName })
        .headers({
          'IM-Environment': siteConfig.environment,
          'IM-SenderID': 'IMX4EC',
          'IM-SiteCode': siteConfig.siteCode,
        });
      const response = await wretcher.get().json<StorefrontInfo>();
      if (active) {
        setValue(response);
      }
    };
    fetchStorefrontInfo();

    return () => {
      active = false;
    };
  }, [buildVars, siteConfig]);

  if (!value) return null;

  const { Provider } = storefrontInfoContext;
  return (
    <Provider value={value}>
      <Helmet>
        <link
          href={value.favicon ?? '/x4ec/assets/img/favicon.ico'}
          rel="icon"
        />
      </Helmet>
      {children}
    </Provider>
  );
}
